import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { BasefieldV2Component } from '@app/formly/types/v2/basefield-v2/basefield-v2.component';
import { DropdownItem } from '@voyagu/radar-design-system';

const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const SHORT_MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const YEAR_LOOKBACK = 10;

@Component({
  selector: 'month-period-field',
  templateUrl: './month-period-field.component.html',
  styleUrls: ['./month-period-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MonthPeriodFieldComponent extends BasefieldV2Component implements OnInit, OnDestroy {
  period = '';
  pickerOpen = false;
  draftMonth = 1;
  draftYear = new Date().getUTCFullYear();
  monthItems: DropdownItem[] = [];
  yearItems: DropdownItem[] = [];
  selectedMonth: DropdownItem | null = null;
  selectedYear: DropdownItem | null = null;

  listenOutsideClicks = false;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private elementRef: ElementRef<HTMLElement>,
  ) {
    super();
  }

  ngOnInit() {
    this.period = (this.formControl.value as string) || (this.props.initial as string) || '';
    if (this.period && this.formControl.value !== this.period) {
      this.formControl.setValue(this.period, { emitEvent: false });
    }
    this.syncDraftFromPeriod();
    this.rebuildPickerOptions();
  }

  ngOnDestroy() {
    this.listenOutsideClicks = false;
  }

  get label() {
    return formatPeriodLabel(this.period);
  }

  get currentPeriod() {
    return (this.props.currentPeriod as string) || '';
  }

  get isCurrent() {
    return !!this.period && this.period === this.currentPeriod;
  }

  get canGoNext() {
    if (!this.currentPeriod) {
      return true;
    }
    return this.period < this.currentPeriod;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.pickerOpen || !this.listenOutsideClicks) {
      return;
    }
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.closePicker();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.pickerOpen) {
      this.closePicker();
    }
  }

  prev() {
    this.setPeriod(shiftPeriod(this.period, -1));
  }

  next() {
    if (!this.canGoNext) {
      return;
    }
    const next = shiftPeriod(this.period, 1);
    if (this.currentPeriod && next > this.currentPeriod) {
      return;
    }
    this.setPeriod(next);
  }

  togglePicker(event: MouseEvent) {
    event.stopPropagation();
    if (this.pickerOpen) {
      this.closePicker();
      return;
    }
    this.openPicker();
  }

  openPicker() {
    this.syncDraftFromPeriod();
    this.rebuildPickerOptions();
    this.pickerOpen = true;
    this.listenOutsideClicks = false;
    this.changeDetectorRef.markForCheck();
    setTimeout(() => {
      this.listenOutsideClicks = true;
    });
  }

  closePicker() {
    this.pickerOpen = false;
    this.listenOutsideClicks = false;
    this.changeDetectorRef.markForCheck();
  }

  onMonthSelect(item: DropdownItem) {
    this.draftMonth = Number(item.value);
    this.selectedMonth = item;
    this.applyDraft();
  }

  onYearSelect(item: DropdownItem) {
    this.draftYear = Number(item.value);
    this.selectedYear = item;
    this.rebuildMonthItems();
    this.applyDraft();
  }

  goToCurrent() {
    if (!this.currentPeriod || this.isCurrent) {
      return;
    }
    this.setPeriod(this.currentPeriod);
    this.closePicker();
  }

  setPeriod(period: string) {
    if (!period || period === this.period) {
      this.syncDraftFromPeriod();
      this.rebuildPickerOptions();
      this.changeDetectorRef.markForCheck();
      return;
    }
    this.period = period;
    this.formControl.setValue(period);
    this.formControl.markAsDirty();
    this.syncDraftFromPeriod();
    this.rebuildPickerOptions();
    this.changeDetectorRef.markForCheck();
  }

  syncDraftFromPeriod() {
    const parsed = parsePeriod(this.period) || parsePeriod(this.currentPeriod);
    if (!parsed) {
      return;
    }
    this.draftYear = parsed.year;
    this.draftMonth = parsed.month;
  }

  rebuildPickerOptions() {
    this.rebuildYearItems();
    this.rebuildMonthItems();
  }

  rebuildYearItems() {
    const current = parsePeriod(this.currentPeriod);
    const maxYear = current?.year ?? new Date().getUTCFullYear();
    const minYear = Math.min(maxYear - YEAR_LOOKBACK, this.draftYear);
    this.yearItems = [];
    for (let year = maxYear; year >= minYear; year -= 1) {
      this.yearItems.push({ label: String(year), value: String(year) });
    }
    this.selectedYear =
      this.yearItems.find((item) => item.value === String(this.draftYear)) || null;
  }

  rebuildMonthItems() {
    const current = parsePeriod(this.currentPeriod);
    const maxMonth =
      current && this.draftYear === current.year ? current.month : MONTH_LABELS.length;

    this.monthItems = MONTH_LABELS.slice(0, maxMonth).map((label, index) => ({
      label,
      value: String(index + 1),
    }));

    if (this.draftMonth > maxMonth) {
      this.draftMonth = maxMonth;
    }

    this.selectedMonth =
      this.monthItems.find((item) => item.value === String(this.draftMonth)) || null;
  }

  applyDraft() {
    const period = toPeriod(this.draftYear, this.draftMonth);
    const current = this.currentPeriod;
    this.setPeriod(current && period > current ? current : period);
  }
}

export function parsePeriod(period: string) {
  const match = /^(\d{4})-(\d{2})$/.exec(period || '');
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) {
    return null;
  }
  return { year, month };
}

export function toPeriod(year: number, month: number) {
  return `${year}-${String(month).padStart(2, '0')}`;
}

export function formatPeriodLabel(period: string) {
  const parsed = parsePeriod(period);
  if (!parsed) {
    return period;
  }
  return `${SHORT_MONTH_LABELS[parsed.month - 1]} ${parsed.year}`;
}

export function shiftPeriod(period: string, delta: number) {
  const parsed = parsePeriod(period);
  if (!parsed) {
    return period;
  }
  const date = new Date(Date.UTC(parsed.year, parsed.month - 1 + delta, 1));
  return toPeriod(date.getUTCFullYear(), date.getUTCMonth() + 1);
}
